/** @jsxImportSource @emotion/react */
import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import dayjs from 'dayjs';
import { v4 as uuid, validate } from 'uuid';
import { Button } from '@mui/material';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { InputDate } from '@grapecity/wijmo.input';
import { DataMap, CellType } from '@grapecity/wijmo.grid';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { tb } from 'components/layouts/Table';
import CustomGrid from 'components/grids/CustomGrid';
import CustomDatepicker from 'components/inputs/CustomDatepicker';
import { CrudCode } from 'models/common/Edit';
import { CommonYN, FileTypeName } from 'models/common/Common';
import { Code } from 'models/common/CommonCode';
import { useMessageBar } from 'hooks/useMessageBar';
import useSessionStore from 'stores/useSessionStore';

import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { findIdleAssetRequestEquipments, getImportExcelData } from 'apis/asst/IdleAsset';

import {
  AsstTpCd,
  IdleAssetDetail,
  IdleAssetRequestDetail,
  IdleAssetRequestMaster,
} from 'models/asst/IdleAsset';

import FileUploadPopUp from 'pages/common/components/FileUploadPopUp';
import { FileuploadBtn } from 'components/buttons/CustomButton';
import WJCellTextarea from 'components/inputs/WJCellTextarea';
import { ComboBox } from 'components/selects/ComboBox';
import ExcelUploadButton from 'components/buttons/ExcelUploadButton';
import ExcelValidModal from 'components/modals/common/ExcelValidModal';
import { useLoading } from '../../../components/process/Loading';

import { CustomEquipment, EquipmentLevel } from 'models/common/popup/EquipmentMaster';
import _, { toNumber } from 'lodash';
import { addClass, DataType, toggleClass } from '@grapecity/wijmo';
import { downloadExcelTemplates, uploadExcelUtMatrixTemplates } from 'apis/common/Excel';
import { useLocation, useSearchParams } from 'react-router-dom';
import { getExcelFileName } from '../../../utils/ExcelUtil';
import { validateLocaleAndSetLanguage } from 'typescript';
import IdlAssetEquipmentPopUp from '../popup/IdlAssetEquipmentPopUp';

interface Props {
  aprReqId?: string;
  rvwReqId: string;
  rvmAsstTpCd?: string;
  isReadOnly?: boolean;
}

/**
 * @desc 불용자산 결재요청
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.01.15   jihwan89       불용자산 결재화면(임시저장, 결재요청 등) 요청법인, 검토기한 오류 수정(저장된게 노충이안된것)
 *                             기타자산 결재요청 시 자산번호 중복 밸리데이션 추가
 */
const IdleAssetRequest = (props: Props, ref) => {
  const { aprReqId, rvwReqId, rvmAsstTpCd, isReadOnly } = props;
  // eslint-disable-next-line react/prop-types
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openLoading } = useLoading();
  const userSession = useSessionStore();
  const gridRef = useRef<any>();
  const [asstTpNm, setAsstTpNm] = useState<any>();

  const dateEditor = useRef(
    new InputDate(document.createElement('div'), {
      format: 'yyyy.MM.dd',
      isRequired: false,
      max: new Date(),
    })
  );
  const [masterData, setMasterData] = useState<IdleAssetRequestMaster>({
    asstRvwReqCopCd: userSession?.elmCopCd,
    rvwCpltDt: dayjs().add(14, 'day').format('YYYY.MM.DD'),
    rvwReqId: rvwReqId,
    asstTpCd: rvmAsstTpCd,
  });

  const [rowData, setRowData] = useState<IdleAssetRequestDetail[]>([]);
  const [code, setCode] = useState<any>();
  const [hitTest, setHitTest] = useState<any>();
  const [isOpenCellTextarea, setOpenCellTextarea] = useState<boolean>(false);
  const [isOpenFileUploadModal, setOpenFileUploadModal] = useState<boolean>(false);
  const [equipmentModalCondition, setEquipmentModalCondition] = useState<any>({});
  const [isOpenEquipmentModal, setOpenEquipmentModal] = useState<boolean>(false);

  const [searchParams] = useSearchParams();
  const [excelValidCondition, setExcelValidCondition] = useState<string | null>(null);
  const [isExcelValidModalOpen, setExcelValidModalOpen] = useState<boolean>(false);

  const [isEQP, setIsEQP] = useState<boolean>(false);

  useEffect(() => {
    getCommonCodes();
    setIsEQP(AsstTpCd.EQUIPMENT == props.rvmAsstTpCd);
  }, []);

  useEffect(() => {
    handleSearch();
  }, [rvwReqId]);

  useImperativeHandle(ref, () => ({
    validate: () => {
      const saveData = rowData.filter((o) => o.crudKey && o.crudKey !== CrudCode.READ);

      if (_.isEmpty(masterData?.asstRvwReqCopCd)) {
        return openMessageBar({
          type: 'error',
          content: `${t('asst.msg.요청법인을 선택해주세요.', '요청법인을 선택해주세요.')}`,
        });
      }
      if (_.isEmpty(masterData?.rvwCpltDt)) {
        return openMessageBar({
          type: 'error',
          content: `${t('asst.msg.검토기한일을 입력해주세요.', '검토기한을 입력해주세요.')}`,
        });
      }

      if (saveData.length == 0 && rowData.length == 0) {
        return openMessageBar({
          type: 'error',
          content: `${t('asst.msg.검토설비를 등록해주세요.', '검토설비를 등록해주세요.')}`,
        });
      }

      const vaildData: any = saveData.length == 0 ? rowData : saveData;

      if (rvmAsstTpCd === 'ETC') {
        const dulplasstNmCheck =
          new Set(vaildData.map((item) => item.asstNo)).size !== vaildData.length;
        if (dulplasstNmCheck) {
          return openMessageBar({
            type: 'error',
            content: `${t('asst.msg.자산번호가 중복입니다.', '자산번호가 중복입니다.')}`,
          });
        }
      }

      const valid = vaildData
        .map((o, index) => {
          if (isEQP && _.isEmpty(o.prdnProcCd)) {
            return `${index + 1} : ${t('asst.msg.공정을 입력해주세요.', '공정을 입력해주세요.')}\n`;
          }
          if (isEQP && _.isEmpty(o.eqpId)) {
            return `${index + 1} : ${t(
              'asst.msg.설비ID를 입력해주세요.',
              '설비명를 입력해주세요.'
            )}\n`;
          }
          if (isEQP && _.isEmpty(o.eqpNm)) {
            return `${index + 1} : ${t(
              'asst.msg.코스트센터를 입력해주세요.',
              '코스트센터를 입력해주세요.'
            )}\n`;
          }

          if (_.isEmpty(o.asstNo)) {
            return `${index + 1} : ${t(
              'asst.msg.자산번호를 입력해주세요.',
              '자산번호를 입력해주세요.'
            )}\n`;
          }
          if (_.isEmpty(o.asstNm)) {
            return `${index + 1} : ${t(
              'asst.msg.고정자산명을 입력해주세요.',
              '고정자산명을 입력해주세요.'
            )}\n`;
          }

          if (isEQP && _.isEmpty(o.cctrCd)) {
            return `${index + 1} : ${t(
              'asst.msg.코스트센터를 입력해주세요.',
              '코스트센터를 입력해주세요.'
            )}\n`;
          }
          if (isEQP && _.isEmpty(o.cctrNm)) {
            return `${index + 1} : ${t(
              'asst.msg.코스트센터명을 입력해주세요.',
              '코스트센터명을 입력해주세요.'
            )}\n`;
          }

          if (!_.isDate(o.acqrDt)) {
            const datePattern = /^(19|20)\d{2}.(0[1-9]|1[012]).(0[1-9]|[12][0-9]|3[0-1])$/;
            //  console.log('reg change  : ' || o.acqrDt || '', datePattern.test(o.acqrDt || ''));
            if (!datePattern.test(o.acqrDt || '')) {
              return `${index + 1} : ${t(
                'asst.msg.자산취득일자를 선택해주세요.',
                '자산취득일자를 선택해주세요.'
              )}\n`;
            }
          }

          if (!_.isNumber(o.asstAcqrAmt)) {
            return `${index + 1} : ${t(
              'asst.msg.취득가액을 입력해주세요.',
              '취득가액을 입력해주세요.'
            )}\n`;
          }
          if (!_.isNumber(o.deprAmt)) {
            return `${index + 1} : ${t(
              'asst.msg.잔존가액을 입력해주세요.',
              '잔존가액을 입력해주세요.'
            )}\n`;
          }
          //잔존가액 deprAmt , 취득가액 asstAcqrAmt 잔존가액이 취득가액 보다 많으면 안됨
          if (toNumber(o.deprAmt) > toNumber(o.asstAcqrAmt)) {
            return `${index + 1} : ${t(
              'asst.msg.잔존가액이 취득가액보다 많습니다.',
              '잔존가액이 취득가액보다 많습니다.'
            )}\n`;
          }
          if (_.isEmpty(o.currCd)) {
            return `${index + 1} : ${t(
              'asst.msg.통화단위을 입력해주세요.',
              '통화단위을 입력해주세요.'
            )}\n`;
          }
        })
        .filter((element) => element !== undefined);

      if (valid.length) {
        const content = valid[0]?.toString();
        openMessageBar({ type: 'warning', content: content || '' });
        return;
      }

      return true;
    },
    getBizReqData: (aprReqId: string, reqRsn: string) => {
      const master = {
        ...masterData,
        castType: 'IdleAssetRequest',
        aprReqId: aprReqId,
        rvwReqId: rvwReqId,
        asstTpCd: rvmAsstTpCd,
        requestDetailList: rowData,
      };
      return master;
    },
  }));

  const getCommonCodes = async () => {
    const elmCopCd: Code[] = await getCommonCodeNames('ELM_COP_CD'); // 법인코드
    const asstTpCd: Code[] = await getCommonCodeNames('ASST_TP_CD'); // 자산유형코드
    const factoryCode: Code[] = await getCommonCodeNames('FACTORY_CODE');
    const currCd: Code[] = await getCommonCodeNames('CURR_CD'); // 통화단위

    const prdnProcCd = await getCommonCodeNamesCondition({
      optValCtn5: 'Y',
      cmnGrCd: 'PRDN_PROC_CD',
    }); // 공정

    //if (_.isEmpty(masterData.asstTpCd)) {
    const detailList = ref.current.getBizReqData().requestDetailList;
    if (detailList != null && detailList.length > 0) {
      const item = masterData;
      item['asstTpCd'] = detailList[0].asstTpCd;
      item['currCd'] = detailList[0].currCd;
      item['atchFileCnt'] = detailList[0].atchFileCnt;
      item['atchFileGrId'] = detailList[0].atchFileGrId;
      item['asstRvwReqCopCd'] = detailList[0].asstRvwReqCopCd;
      item['rvwCpltDt'] = detailList[0].rvwCpltDt;
      setMasterData(item);
    }
    //}

    if (_.isEmpty(masterData.currCd)) {
      setMasterData({ ...masterData, currCd: 'USD' });
    }

    const asstTpCdItem: any = asstTpCd.find((item) => item.cmnCd == masterData.asstTpCd);
    if (asstTpCdItem) {
      setAsstTpNm(asstTpCdItem.cmnCdNm);
      setIsEQP(AsstTpCd.EQUIPMENT == masterData.asstTpCd);
    }

    setCode({
      elmCopCd: elmCopCd,
      asstTpCd: asstTpCd,
      factoryCode: factoryCode,
      prdnProcCd: prdnProcCd,
      currCd: currCd,
    });
  };
  const handleExportExcel = async (templateType) => {
    if (templateType == null || _.isEmpty(templateType)) {
      const zExcelfileNm = isEQP
        ? t('asst.label.요청 설비 상세 내용', '요청 설비 상세 내용')
        : t('asst.label.요청 기타자산 상세 내용', '요청 기타자산 상세 내용');

      const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(gridRef.current, {
        includeColumnHeaders: true,
        includeRowHeaders: true,
      });
      book.sheets[0].name = zExcelfileNm; //t('asst.label.불용자산 등록 List', '불용자산 등록 List');
      book.saveAsync(getExcelFileName('[' + rvwReqId + '] ' + zExcelfileNm));
    } else {
      let _templateType = FileTypeName.TPL_IDL_ASST_UPLOAD;
      if (rvmAsstTpCd === AsstTpCd.ETC_ASST) {
        _templateType = FileTypeName.TPL_IDL_ETC_UPLOAD;
      }
      downloadExcelTemplates(_templateType, rvwReqId);
    }
  };

  const handleImportExcel = (params: any) => {
    getImportExcelData(params.p_xls_upload_id).then((result) => {
      const insertResult: any = result.map((item) => ({
        ...item,
        crudKey: CrudCode.CREATE,
        asstQty: 1,
        asstQtyUnitNm: 'EA',
        asstTpCd: masterData.asstTpCd,
        eqpOwnCopCd: masterData?.asstRvwReqCopCd,
      }));
      //-- eqpOwnCopCd: masterData?.asstRvwReqCopCd,
      setRowData(insertResult);
    });
  };

  const handleChangeRowData = (column: string, value: any) => {
    const obj = masterData;
    obj[column] = value;
    setMasterData(obj);
    setRowData(
      rowData.map(function (item) {
        if ('asstRvwReqCopCd' == column) {
          item['eqpOwnCopCd'] = value;
        }
        item[column] = value;

        return item;
      })
    );
  };

  const handleAddRow = () => {
    const newRow = {
      crudKey: CrudCode.CREATE,
      asstTpCd: masterData.asstTpCd,
      eqpOwnCopCd: masterData?.asstRvwReqCopCd,
      useYn: CommonYN.Y,
      asstQty: '1',
      asstQtyUnitNm: 'EA',
      currCd: masterData?.currCd,
    } as IdleAssetRequestDetail;
    setRowData([newRow, ...rowData]);
  };

  const handleDelRow = () => {
    const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);
    if (!selectedRows) return;

    const selectedIds = selectedRows
      .map((row) => {
        return parseInt(row.index!);
      })
      .reverse();

    selectedIds.forEach((item) => {
      if (rowData[item].crudKey === CrudCode.CREATE) {
        delete rowData[item];
      } else {
        rowData[item].crudKey = CrudCode.DELETE;
      }
    });

    const filteredData = rowData.filter((element) => {
      if (element !== undefined && element.crudKey != CrudCode.DELETE) {
        return element;
      }
    });
    setRowData(filteredData);
  };

  const handleSearch = () => {
    if (!rvwReqId || !props?.aprReqId) {
      return;
    }
    findIdleAssetRequestEquipments(rvwReqId, props?.aprReqId).then((result) => {
      setRowData(result);
    });
  };

  const handleFileUpload = async (item, row, col) => {
    setFileUploadModalCondition({
      atchFileGrId: item.atchFileGrId,
      tableName: 'tb_eelmb_idl_asst_d',
      atchFileTpCd: 'IDL_REFERENCES',
      bizName: 'asst',

      target: {
        item: item,
        row: row,
        col: col,
      },
    });
    setOpenFileUploadModal(true);
  };

  const onCellEditEnded = (grid, e) => {
    const binding = grid.columns[e.col].binding;
    if (AsstTpCd.EQUIPMENT != masterData.asstTpCd) {
      const data = grid.rows[e.row].dataItem;
      if ('asstNo' === binding) {
        data.eqpId = data.asstNo;
        grid.refreshCells();
      }
      if ('asstNm' === binding) {
        data.eqpNm = data.asstNm;
        grid.refreshCells();
      }
    }
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.ColumnHeader === panel.cellType) {
      const binding = panel.columns[col].binding;
      // 필수항목
      if (
        ['prdnProcCd', 'asstNo', 'asstNm', 'acqrDt', 'asstAcqrAmt', 'deprAmt', 'currCd'].includes(
          binding
        ) ||
        (isEQP && ['cctrCd', 'cctrNm', 'eqpId', 'eqpNm'].includes(binding))
      ) {
        addClass(cell, 'dot');
      }
    }

    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
      // 비고 editable css 변경
      if (['rmk'].includes(binding)) {
        cell.ariaReadOnly = isReadOnly;
      }
    }
  });

  const onInitialized = (grid) => {
    gridRef.current = grid;

    if (isReadOnly == false) grid.itemFormatter = onItemFormatter;

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);

      if (ht.row < 0 || ht.col < 0) return;

      if (ht.panel === grid.cells) {
        const item = grid.rows[ht.row].dataItem;
        const binding = grid.columns[ht.col].binding;

        // 첨부파일 클릭
        if ('atchFileCnt' === binding) {
          handleFileUpload(item, ht.row, ht.col);
        }
        // 비고 클릭
        else if ('rmk' === binding) {
          setHitTest(ht);
          setOpenCellTextarea(true);
        }
      }
    });
  };
  const [isOpenFileUploadPopUp, setOpenFileUploadPopUp] = useState<boolean>(false);
  const [fileUploadModalCondition, setFileUploadModalCondition] = useState<any>();
  const handleFileUploadBtn = async () => {
    const downloadOnly = false;
    const fileCount = 0;
    // fileCount = Number(detail?.fileCount || 0);
    /*
    const atchFileGrId = masterData?.atchFileGrId;
    if (_.isEmpty(atchFileGrId)) {
      if (atchFileGrId) {
        masterData.atchFileGrId = atchFileGrId;
      } else {
        openMessageBar({
          type: 'error',
          content:
            t(
              'com.label.첨부파일그룹ID 생성에 실패했습니다.',
              '첨부파일그룹ID 생성에 실패했습니다.'
            ) +
            <br /> +
            t('com.label.새로고침 후 다시 시도해 주세요.', '새로고침 후 다시 시도해 주세요.'),
        });
        return;
      }
    }

    atchFileTpCd: 'ASST_IDL_VIEW',
      tableName: 'tb_eelmb_idl_asst_m',


    */

    setFileUploadModalCondition({
      atchFileGrId: masterData?.atchFileGrId,
      atchFileTpCd: 'ASST_IDL_VIEW',
      tableName: 'tb_elm_apr_req_m',
      bizName: 'appr',
    });
    setOpenFileUploadPopUp(true);
  };

  const layoutDefinition = [
    {
      binding: 'no',
      header: String(t('com.label.NO', 'NO')),
      align: 'center',
      width: 40,
      isReadOnly: true,
      cellTemplate: (grid) => grid.row._idx + 1,
    },
    {
      binding: 'prdnProcCd',
      header: String(t('asst.label.공정', '공정')),
      align: 'left',
      width: 100,
      dataMap: new DataMap(code?.prdnProcCd || [], 'cmnCd', 'cmnCdNm'),
      isReadOnly: isEQP,
    },
    {
      binding: 'eqpId',
      header: String(t('asst.label.설비ID', '설비ID')),
      align: 'left',
      width: 100,
      isReadOnly: true,
      visible: isEQP,
    },
    {
      binding: 'eqpNm',
      header: String(t('asst.label.설비명', '설비명')),
      align: 'left',
      width: 100,
      isReadOnly: true,
      visible: isEQP,
    },
    {
      binding: 'asstNo',
      header: String(t('asst.label.자산번호', '자산번호')),
      align: 'left',
      width: 100,
      maxLength: 50,
    },
    {
      binding: 'asstNm',
      header: String(t('asst.label.고정자산명', '고정자산명')),
      align: 'left',
      width: 100,
      maxLength: 300,
    },
    {
      binding: 'cctrCd',
      header: String(t('asst.label.코스트센터', '코스트센터')),
      align: 'left',
      width: 100,
      isRequired: !isEQP,
      maxLength: 10,
    },
    {
      binding: 'cctrNm',
      header: String(t('asst.label.코스트센터명', '코스트센터명')),
      align: 'left',
      width: 100,
      isRequired: !isEQP,
      maxLength: 200,
    },
    {
      binding: 'acqrDt',
      header: String(t('asst.label.자산취득일자', '자산취득일자')),
      align: 'left',
      width: 100,
      cssClass: 'WijmoDate',

      editor: dateEditor.current,
      format: 'yyyy.MM.dd',
      cellTemplate: (params) => (params.value ? dayjs(params.value).format('YYYY.MM.DD') : ''),
      // isReadOnly: false, // 컬럼 속성에서 isReadOnly 설정을 하는 경우 달력 아이콘 미노출되어 별도로 이벤트 제어
    },
    {
      binding: 'mdlNm',
      header: String(t('asst.label.대응모델', '대응모델')),
      align: 'left',
      width: 100,
      maxLength: 200,
      isRequired: false,
    },
    {
      binding: 'asstAcqrAmt',
      header: String(t('asst.label.취득가액', '취득가액')),
      align: 'right',
      width: 100,
      dataType: DataType.Number,
    },
    {
      binding: 'deprAmt',
      header: String(t('asst.label.잔존가액', '잔존가액')),
      align: 'right',
      width: 100,
      dataType: DataType.Number,
    },
    /* {
      binding: 'crudKey',
      header: String(t('com.label.상태', '상태')),
      width: 50,
      isReadOnly: true,
      cellTemplate: GridStatusCellTemplate,
    },
    {
      binding: 'eqpIdExstYn',
      width: 80,
      header: String(t('asst.label.구분', '구분')),
      isReadOnly: true,
      align: 'center',
      cssClass: 'WijmoCheck',
      cellTemplate: (params) => {
        const checked = params.value === 'Y' ? 'checked' : '';
        return `<div class="checkbox">
                  <input type="checkbox" id="eqpIdExstYn_${uuid()}" ${checked} disabled />
                  <label />
                </div>
          `;
      },
    },


    {
      binding: 'prdnPldoCd',
      header: String(t('asst.label.플랜트코드', '플랜트코드')),
      align: 'center',
      dataMap: new DataMap(code?.factoryCode || [], 'cmnCd', 'cmnCdNm'),
      isReadOnly: true,
    },


*/

    {
      binding: 'asstQty',
      header: String(t('asst.label.수량', '수량')),
      align: 'center',
      width: 100,
      visible: false,
    },

    {
      binding: 'asstQtyUnitNm',
      header: String(t('asst.label.수량', '수량')),
      align: 'center',
      width: 100,
      visible: false,
    },
    {
      binding: 'currCd',
      header: String(t('asst.label.통화단위', '통화단위')),
      align: 'center',
      width: 100,
      maxLength: 100,
      isReadOnly: true,
    },
    /*{
      binding: 'eqpOwnCopCd',
      header: String(t('asst.label.법인', '법인')),
      align: 'center',
      width: 100,
      dataMap: new DataMap(code?.elmCopCd || [], 'cmnCd', 'cmnCdNm'),
      isReadOnly: true,
    },
    {
      binding: 'atchFileCnt',
      header: String(t('asst.label.참고자료', '참고자료')),
      align: 'center',
      width: 100,
      isReadOnly: true,

      cellTemplate: (params) =>
        `<div class="fileDiv"><span><em>${(params.value || 0).toLocaleString()}</em></span></div>`,
    },*/
    {
      binding: 'rmk',
      header: String(t('asst.label.비고', '비고')),
      align: 'left',
      width: '*',
      isReadOnly: true,
    },
    // {
    //   binding: 'atchFileGrId',
    //   visible: false,
    // },
    {
      binding: 'asstTpCd',
      visible: false,
    },
  ];

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('asst.label.요청 정보', '요청 정보')}</h3>
        </SubTitleGroup>
      </SubTitleLayout>
      <TableContainer css={tb.table} className="colFix3" style={{ marginBottom: '24px' }}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>
                <span>{t('asst.label.요청번호', '요청번호')}</span>
              </TableCell>
              <TableCell>{rvwReqId}</TableCell>
              <TableCell>
                <span className={isReadOnly ? '' : 'dot'}>
                  {t('asst.label.요청법인', '요청법인')}
                </span>
              </TableCell>
              <TableCell>
                {!isReadOnly ? (
                  <ComboBox
                    placeholder={String(
                      t('asst.label.요청법인을 선택해주세요.', '요청법인을 선택해주세요.')
                    )}
                    options={code?.elmCopCd}
                    defaultValue={masterData?.asstRvwReqCopCd}
                    onChange={(value: string) => handleChangeRowData('asstRvwReqCopCd', value)}
                  />
                ) : (
                  masterData?.asstRvwReqCopCd
                )}
              </TableCell>

              <TableCell>
                <span>{t('asst.label.분류', '분류')}</span>
              </TableCell>
              <TableCell>{asstTpNm}</TableCell>

              <TableCell>
                <span>{t('asst.label.첨부파일', '첨부파일')}</span>
              </TableCell>
              <TableCell>
                <FileuploadBtn onClick={handleFileUploadBtn}>
                  <span>{Number(masterData?.atchFileCnt || 0).toLocaleString()}</span>
                </FileuploadBtn>
              </TableCell>

              <TableCell>
                <span className={isReadOnly ? '' : 'dot'}>
                  {t('asst.label.통화단위', '통화단위')}
                </span>
              </TableCell>
              <TableCell>
                {!isReadOnly ? (
                  <ComboBox
                    placeholder={String(
                      t('asst.label.통화단위 선택해주세요.', '통화단위을 선택해주세요.')
                    )}
                    options={code?.currCd}
                    defaultValue={masterData?.currCd}
                    onChange={(value: string) => {
                      handleChangeRowData('currCd', value);
                    }}
                  />
                ) : (
                  masterData?.currCd
                )}
              </TableCell>
              <TableCell>
                <span className={isReadOnly ? '' : 'dot'}>
                  {t('asst.label.검토기한', '검토기한')}
                </span>
              </TableCell>
              <TableCell>
                <CustomDatepicker
                  disabled={isReadOnly}
                  isRange={false}
                  value={masterData?.rvwCpltDt}
                  minDate={dayjs().toDate()}
                  onChange={(newValue) => {
                    setMasterData((prev) => ({
                      ...prev,
                      rvwCpltDt: newValue as string,
                    }));
                  }}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>
            {isEQP
              ? t('asst.label.요청 설비 상세 내용', '요청 설비 상세 내용')
              : t('asst.label.요청 기타자산 상세 내용', '요청 기타자산 상세 내용')}
          </h3>
          <span className="total">
            {t('com.label.총', '총')} <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
          {/* <div className="info warning">
            {t(
              'asst.msg.취득가 및 잔존가는 USD($)로 입력하세요.',
              '취득가 및 잔존가는 USD($)로 입력하세요.'
            )}
          </div>*/}
        </SubTitleGroup>
        <ControlBtnGroup>
          {!isReadOnly && (
            <ExcelUploadButton
              msgConfirm={
                t(
                  'com.msg.저장된 데이터가 있으면 기존 데이터는 삭제됩니다. 새로 Upload 하시겠습니까?',
                  '저장된 데이터가 있으면 기존 데이터는 삭제됩니다. 새로 Upload 하시겠습니까?'
                ) || ''
              }
              onCallback={(file) => {
                openLoading(true);
                uploadExcelUtMatrixTemplates(
                  file,
                  'TPL_IDL_ASST_UPLOAD',
                  'NORMAL',
                  'TB_EELMB_IDL_ASST_REQ_D',
                  masterData?.rvwReqId,
                  masterData?.asstTpCd
                )
                  .then((response) => {
                    openLoading(false);
                    if (response?.data.x_result == 'OK') {
                      openMessageBar({
                        type: 'confirm',
                        content: t('asst.msg.검증이 완료되었습니다.', '검증이 완료되었습니다.'),
                      });
                      handleImportExcel(response?.data);
                    } else {
                      setExcelValidCondition(response?.data.p_xls_upload_id);
                      setExcelValidModalOpen(true);
                    }
                  })
                  .finally(() => openLoading(false));
              }}
            />
          )}
          {!isReadOnly && (
            <Button
              css={IconButton.button}
              className="Exceldown"
              //   onClick={handleExportExcel}
              onClick={() => handleExportExcel(FileTypeName.TPL_IDL_ASST_UPLOAD)}
              disableRipple
            >
              {t('com.button.템플릿 다운로드', '템플릿 다운로드')}
            </Button>
          )}
          {!isReadOnly && (
            <Button
              css={IconButton.button}
              className="addRow"
              onClick={() => {
                if (isEQP) {
                  if (!masterData.asstRvwReqCopCd) {
                    openMessageBar({
                      type: 'error',
                      content: `${t(
                        'asst.msg.요청법인을 선택해 주세요.',
                        '요청법인을 선택해 주세요.'
                      )}`,
                    });
                  } else {
                    setEquipmentModalCondition({});
                    setOpenEquipmentModal(true);
                  }
                } else {
                  handleAddRow();
                }
              }}
              disableRipple
            >
              {isEQP ? t('asst.label.설비추가', '설비추가') : t('asst.label.행추가', '행추가')}
            </Button>
          )}
          {!isReadOnly && (
            <Button
              css={IconButton.button}
              className="delRow"
              onClick={() => handleDelRow()}
              disableRipple
            >
              {t('com.button.행삭제', '행삭제')}
            </Button>
          )}
          {/*  <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch()}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>*/}
          <Button
            css={IconButton.button}
            className="Exceldown"
            //   onClick={handleExportExcel}
            onClick={() => handleExportExcel(null)}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      {isReadOnly && (
        <CustomGrid
          layoutDefinition={layoutDefinition}
          rowData={rowData}
          height={150}
          // excludeFilter={['eqpIdExstYn', 'atchFileCnt']} 1
          isFilter={false}
          allowPinning={false}
          initialized={onInitialized}
          cellEditEnded={onCellEditEnded}
          isSelector={false}
          isReadOnly={isReadOnly}
        />
      )}

      {!isReadOnly && (
        <CustomGrid
          layoutDefinition={layoutDefinition}
          rowData={rowData}
          height={150}
          // excludeFilter={['eqpIdExstYn', 'atchFileCnt']} 1
          isFilter={false}
          allowPinning={false}
          initialized={onInitialized}
          cellEditEnded={onCellEditEnded}
          isSelector={true}
          isReadOnly={isReadOnly}
        />
      )}

      {isExcelValidModalOpen && (
        <ExcelValidModal
          open={isExcelValidModalOpen}
          close={() => {
            setExcelValidCondition(null);
            setExcelValidModalOpen(false);
          }}
          condition={{
            valReqId: excelValidCondition || '',
          }}
        />
      )}

      {isOpenCellTextarea && (
        <WJCellTextarea
          grid={gridRef.current}
          hitTest={hitTest}
          close={() => setOpenCellTextarea(false)}
        />
      )}
      {/* 첨부파일 팝업 */}
      {isOpenFileUploadPopUp && (
        <FileUploadPopUp
          open={isOpenFileUploadPopUp}
          close={() => setOpenFileUploadPopUp(false)}
          // downloadOnly={fileUploadModalCondition?.downloadOnly}
          initParam={{
            atchFileGrId: fileUploadModalCondition?.atchFileGrId,
            atchFileTpCd: fileUploadModalCondition?.atchFileTpCd,
            optValCtn1: fileUploadModalCondition?.tableName,
            bizName: fileUploadModalCondition?.bizName,
          }}
          onCallback={(atchFileGrId, fileCount) => {
            // masterData.atchFileCnt = fileCount; //파일개수
            // masterData.atchFileGrId = atchFileGrId as string; //  첨부파일그룹ID
            handleChangeRowData('atchFileCnt', fileCount);
            handleChangeRowData('atchFileGrId', atchFileGrId as string);

            setOpenFileUploadPopUp(false);
          }}
        />
      )}

      {isOpenFileUploadModal && (
        <FileUploadPopUp
          open={isOpenFileUploadModal}
          close={() => setOpenFileUploadModal(false)}
          initParam={{
            atchFileGrId: fileUploadModalCondition.atchFileGrId,
            atchFileTpCd: fileUploadModalCondition.atchFileTpCd,
            optValCtn1: fileUploadModalCondition.tableName,
            bizName: fileUploadModalCondition.bizName,
          }}
          onCallback={(atchFileGrId, fileCount) => {
            const target = fileUploadModalCondition.target;

            if (target) {
              const item = gridRef.current?.rows[target.row].dataItem;
              item.atchFileGrId = atchFileGrId;
              gridRef.current?.setCellData(target.row, target.col, fileCount);
              gridRef.current?.startEditing(false, target.row, target.col);
              gridRef.current?.finishEditing();
            }
            setOpenFileUploadModal(false);
          }}
        />
      )}

      {isOpenEquipmentModal && (
        <IdlAssetEquipmentPopUp
          open={isOpenEquipmentModal}
          close={() => setOpenEquipmentModal(false)}
          title={t('asst.label.설비추가', '설비추가')}
          condition={{
            copCd: masterData?.asstRvwReqCopCd || '',
            equipmentLevel: EquipmentLevel.MAIN, // mp 변경으로 인해 기본 설비값 고정
          }}
          isCopCdFix={true}
          isFixedMainLevel={true}
          isCortCrgeUser={false}
          isMpItem={false}
          editable={true}
          singleSelect={false}
          defaultEquipment={(rowData || []).reduce(
            (acc, cur) => [
              ...acc,
              {
                ...cur,
                equipmentId: cur.eqpId,
                equipmentName: cur.eqpNm,
                equipmentDesc: cur.eqpNm,
                factoryCode: cur.prdnPldoCd,
                processCode: cur.prdnProcCd,
                copCd: cur.eqpOwnCopCd,
              },
            ],
            [] as CustomEquipment[]
          )}
          onCallback={(result) => {
            const equipments = (result || []) as CustomEquipment[];
            const eqpList = result.eqpList;

            setRowData(
              eqpList.reduce(
                (acc, cur) => [
                  ...acc,
                  {
                    ...cur,
                    eqpId: cur.equipmentId,
                    eqpNm: cur.equipmentDesc,
                    asstTpCd: AsstTpCd.EQUIPMENT, // [25.01.14] 현재 자산유형이 1건밖에 없어서 초기값 고정
                    asstQty: '1', //수량  1 설비, 기타자산은 무조건 1 임
                    prdnPldoCd: cur.factoryCode,
                    prdnProcCd: cur.processCode,
                    eqpIdExstYn: cur.eqpIdExstYn,
                    currCd: masterData?.currCd,
                    useYn: CommonYN.Y,
                    eqpOwnCopCd: cur.copCd,
                  },
                ],
                [] as IdleAssetDetail[]
              )
            );
          }}
        />
      )}
    </>
  );
};

export default forwardRef(IdleAssetRequest);
